interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
  color?: string;
  fillOpacity?: number;
  ariaLabel?: string;
}

/**
 * Pure-SVG mini line sparkline. No charting library; safe to render
 * in dense table rows.
 */
export function Sparkline({
  values,
  width = 80,
  height = 24,
  color = 'hsl(var(--primary))',
  fillOpacity = 0.15,
  ariaLabel,
}: SparklineProps) {
  if (values.length === 0) {
    return (
      <span
        aria-label={ariaLabel ?? 'No data'}
        style={{ width, height, display: 'inline-block' }}
      />
    );
  }

  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;

  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * height;
    return { x, y };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');
  const areaPath = `${linePath} L ${(width).toFixed(2)} ${height} L 0 ${height} Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel ?? `sparkline of ${values.length} values`}
      style={{ display: 'inline-block' }}
    >
      <path d={areaPath} fill={color} fillOpacity={fillOpacity} />
      <path d={linePath} fill="none" stroke={color} strokeWidth={1.25} />
    </svg>
  );
}
